import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChefHat, Loader2 } from 'lucide-react';
import { showErrorToast } from '@/utils/toast-utils';
import { useRecipeForm } from '@/hooks/recipes/useRecipeForm';
import { useIngredientManagement } from '@/hooks/recipes/useIngredientManagement';
import { useRecipeCreation } from '@/hooks/recipes/useRecipeCreation';
import { useRecipePortionSizes } from '@/hooks/recipes/useRecipePortionSizes';
import { RecipeInformationForm } from '@/components/recipes/RecipeInformationForm';
import { RecipePortionSizesSection } from '@/components/recipes/RecipePortionSizesSection';
import { RecipeTagsSection } from '@/components/recipes/RecipeTagsSection';
import { IngredientsSection } from '@/components/recipes/IngredientsSection';
import { InstructionsSection } from '@/components/recipes/InstructionsSection';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import type { Ingredient } from '@/components/recipes/IngredientList';
import {
  deriveAllergensFromIngredients,
  deriveIntolerancesFromIngredients,
  mergeRecipeAllergens,
  mergeRecipeIntolerances,
  normalizeRecipeAllergenList,
  normalizeRecipeIntoleranceList,
} from '@/utils/recipe-allergens';
import { MEAL_TYPE_OPTIONS } from '@/constants/dietary-goals';

const resolveMealTypeFromSlotName = (slotName: string): string | null => {
  const normalized = slotName.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) return null;

  const exact = MEAL_TYPE_OPTIONS.find((option) => (
    option.value === normalized
    || option.name.toLowerCase() === slotName.trim().toLowerCase()
  ));
  if (exact) return exact.value;

  // Common slot labels like "Morning Snack" → snack
  const partial = MEAL_TYPE_OPTIONS.find((option) => (
    normalized.includes(option.value) || slotName.trim().toLowerCase().includes(option.name.toLowerCase())
  ));
  return partial?.value ?? null;
};

const createRecipeActionTourSteps: ActionTourStep[] = [
  {
    id: 'create-recipe-info',
    title: 'Recipe Information',
    description: 'Start here with the recipe name, description, serving count, meal type, preparation time, and sharing setting.',
  },
  {
    id: 'create-recipe-portions',
    title: 'Portion Sizes',
    description: 'Define how one serving is measured so nutrition totals and serving labels stay accurate.',
  },
  {
    id: 'create-recipe-tags',
    title: 'Tags',
    description: 'Add allergy, intolerance, cuisine, and diet labels so the recipe can be filtered and assigned correctly later.',
  },
  {
    id: 'create-recipe-ingredients',
    title: 'Ingredients',
    description: 'Add each ingredient with amount, unit, yield, and retention details. Nutrition is calculated from this section.',
  },
  {
    id: 'create-recipe-instructions',
    title: 'Instructions',
    description: 'Write the preparation steps and cooking tips clients or practitioners should follow.',
  },
  {
    id: 'create-recipe-submit',
    title: 'Save Recipe',
    description: 'After required details and ingredients are complete, use this button to create the recipe and return it to the library.',
  },
];

const CreateCustomRecipePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const draftId = searchParams.get('draftId') || '';
  const day = searchParams.get('day') || '';
  const mealName = searchParams.get('mealName') || '';
  const fromPath = searchParams.get('from')?.trim() || '';
  const mealPlanReturnPath = fromPath.startsWith('/') ? fromPath : '';
  const isFromMealPlan = Boolean(mealPlanReturnPath || (draftId && clientId));

  const handleBackToMealPlan = useCallback(() => {
    if (mealPlanReturnPath) {
      navigate(mealPlanReturnPath);
      return;
    }
    if (draftId && clientId) {
      const params = new URLSearchParams();
      if (day) params.set('day', day);
      if (mealName) params.set('mealName', mealName);
      const query = params.toString();
      navigate(
        query
          ? `/manual-meal-plan/${draftId}/client/${clientId}?${query}`
          : `/manual-meal-plan/${draftId}/client/${clientId}`,
      );
      return;
    }
    navigate(-1);
  }, [clientId, day, draftId, mealName, mealPlanReturnPath, navigate]);

  const formState = useRecipeForm();
  const portionSizes = useRecipePortionSizes(formState.dishTypes);
  const ingredientManagement = useIngredientManagement({
    searchSource: 'all',
  });

  useEffect(() => {
    if (!isFromMealPlan || !mealName || formState.mealTypes.length > 0) return;
    const resolvedMealType = resolveMealTypeFromSlotName(mealName);
    if (resolvedMealType) {
      formState.setMealTypes([resolvedMealType]);
    }
  }, [formState.mealTypes.length, formState.setMealTypes, isFromMealPlan, mealName]);
  const manualAllergiesRef = useRef<string[]>([]);
  const manualIntolerancesRef = useRef<string[]>([]);
  const autoManagedAllergiesRef = useRef<Set<string>>(new Set());
  const autoManagedIntolerancesRef = useRef<Set<string>>(new Set());
  const ingredientDerivedAllergies = React.useMemo(
    () => deriveAllergensFromIngredients(ingredientManagement.ingredients),
    [ingredientManagement.ingredients],
  );
  const ingredientDerivedIntolerances = React.useMemo(
    () => deriveIntolerancesFromIngredients(ingredientManagement.ingredients),
    [ingredientManagement.ingredients],
  );

  const handleAllergiesChange = useCallback((values: string[]) => {
    const derivedSet = new Set(ingredientDerivedAllergies);
    const autoManagedSet = autoManagedAllergiesRef.current;
    const manualAllergies = normalizeRecipeAllergenList(values).filter((value) => (
      !derivedSet.has(value) && !autoManagedSet.has(value)
    ));
    manualAllergiesRef.current = manualAllergies;
    formState.setAllergies(mergeRecipeAllergens(manualAllergies, ingredientDerivedAllergies));
  }, [formState.setAllergies, ingredientDerivedAllergies]);

  const handleIntoleranceTagsChange = useCallback((values: string[]) => {
    const derivedSet = new Set(ingredientDerivedIntolerances);
    const autoManagedSet = autoManagedIntolerancesRef.current;
    const manualIntolerances = normalizeRecipeIntoleranceList(values).filter((value) => (
      !derivedSet.has(value) && !autoManagedSet.has(value)
    ));
    manualIntolerancesRef.current = manualIntolerances;
    formState.setIntoleranceTags(mergeRecipeIntolerances(manualIntolerances, ingredientDerivedIntolerances));
  }, [formState.setIntoleranceTags, ingredientDerivedIntolerances]);

  useEffect(() => {
    const autoManagedSet = autoManagedAllergiesRef.current;
    manualAllergiesRef.current = normalizeRecipeAllergenList(manualAllergiesRef.current)
      .filter((value) => !autoManagedSet.has(value));

    const manualSet = new Set(manualAllergiesRef.current);
    autoManagedAllergiesRef.current = new Set(
      ingredientDerivedAllergies.filter((value) => !manualSet.has(value)),
    );

    formState.setAllergies(mergeRecipeAllergens(manualAllergiesRef.current, ingredientDerivedAllergies));
  }, [formState.setAllergies, ingredientDerivedAllergies]);

  useEffect(() => {
    const autoManagedSet = autoManagedIntolerancesRef.current;
    manualIntolerancesRef.current = normalizeRecipeIntoleranceList(manualIntolerancesRef.current)
      .filter((value) => !autoManagedSet.has(value));

    const manualSet = new Set(manualIntolerancesRef.current);
    autoManagedIntolerancesRef.current = new Set(
      ingredientDerivedIntolerances.filter((value) => !manualSet.has(value)),
    );

    formState.setIntoleranceTags(mergeRecipeIntolerances(manualIntolerancesRef.current, ingredientDerivedIntolerances));
  }, [formState.setIntoleranceTags, ingredientDerivedIntolerances]);

  const [recipeImagePreviewUrl, setRecipeImagePreviewUrl] = useState<string | null>(null);
  const recipeImageObjectUrlRef = useRef<string | null>(null);
  const recipeImageFileRef = useRef<File | null>(null);

  const revokeRecipeImagePreview = useCallback(() => {
    if (recipeImageObjectUrlRef.current) {
      URL.revokeObjectURL(recipeImageObjectUrlRef.current);
      recipeImageObjectUrlRef.current = null;
    }
    recipeImageFileRef.current = null;
    setRecipeImagePreviewUrl(null);
  }, []);

  useEffect(() => () => revokeRecipeImagePreview(), [revokeRecipeImagePreview]);

  const handleRecipeImageFileSelect = useCallback(
    (file: File | null) => {
      if (!file) {
        revokeRecipeImagePreview();
        return;
      }
      const okType =
        file.type === 'image/jpeg'
        || file.type === 'image/jpg'
        || file.type === 'image/png'
        || file.type === 'image/webp';
      if (!okType) {
        showErrorToast(new Error('Please choose a JPEG, PNG, or WebP image.'), 'Invalid file');
        return;
      }
      const maxBytes = 5 * 1024 * 1024;
      if (file.size > maxBytes) {
        showErrorToast(new Error('Image must be 5MB or smaller.'), 'File too large');
        return;
      }
      revokeRecipeImagePreview();
      recipeImageFileRef.current = file;
      const url = URL.createObjectURL(file);
      recipeImageObjectUrlRef.current = url;
      setRecipeImagePreviewUrl(url);
    },
    [revokeRecipeImagePreview]
  );

  const {
    isCreating,
    handleCreateRecipe,
    duplicateRecipeMessage,
    clearDuplicateRecipeMessage,
  } = useRecipeCreation({
    formState: {
      ...formState,
      portionSizeId: portionSizes.portionSizeId,
      portionSizes: portionSizes.portionSizes,
    },
    ingredients: ingredientManagement.ingredients,
    draftId,
    clientId,
    day,
    mealName,
    returnTo: mealPlanReturnPath,
    recipeImageFileRef,
    manualAllergies: manualAllergiesRef.current,
    manualIntoleranceTags: manualIntolerancesRef.current,
  });

  return (
    <Box data-demo-id="create-recipe-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          bgcolor: '#FBFBFB', 
        }}
      >
        <div className="min-h-full bg-[#FBFBFB]">
          <div className="h-full w-full px-8 py-8">
            <div
              className="w-full min-w-0"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
        {isFromMealPlan ? (
          <div className="mb-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 h-9 gap-1.5 px-2 text-slate-600 hover:text-slate-900"
              onClick={handleBackToMealPlan}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Meal Plan
            </Button>
          </div>
        ) : (
          <div className="mb-6 flex w-full min-w-0 items-center gap-2 rounded-lg border border-[#0000001A] px-3 py-2 text-[13px] text-slate-400">
            <Link to="/recipes" className="shrink-0 hover:text-slate-600">
              Recipes
            </Link>
            <span className="shrink-0">/</span>
            <span className="truncate font-medium text-slate-800">Create Recipe</span>
          </div>
        )}
        <h1
          className="text-[32px] font-medium capitalize leading-tight text-slate-900"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          Create Recipe
        </h1>
        {isFromMealPlan && mealName ? (
          <p className="mt-1 text-sm text-slate-500">
            Adding to {mealName}
          </p>
        ) : null}
        <Accordion
          type="multiple"
          defaultValue={["basic", "portions", "categories", "ingredients", "instructions"]}
          className="mt-6 flex flex-col gap-[35px] overflow-visible"
        >
          <AccordionItem
            data-demo-id="recipe-info-section"
            data-action-tour-id="create-recipe-info"
            value="basic"
            className="overflow-visible rounded-xl border border-[#E5E7EB] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="px-5 py-4 text-[24px] font-medium text-slate-900 hover:no-underline sm:px-6"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Recipe Information
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-10 pt-2 sm:px-6">
              <RecipeInformationForm
                recipeName={formState.recipeName}
                aliases={formState.aliases}
                description={formState.description}
                servings={formState.servings}
                dishTypes={formState.dishTypes}
                mealTypes={formState.mealTypes}
                cookingMethod={formState.cookingMethod}
                prepTimeMinutes={formState.prepTimeMinutes}
                cookTimeMinutes={formState.cookTimeMinutes}
                totalTimeMinutes={formState.totalTimeMinutes}
                shareWithOthers={formState.shareWithOthers}
                recipeImagePreviewUrl={recipeImagePreviewUrl}
                onRecipeNameChange={formState.setRecipeName}
                onAliasesChange={formState.setAliases}
                onDescriptionChange={formState.setDescription}
                onServingsChange={formState.setServings}
                onDishTypesChange={formState.setDishTypes}
                onMealTypesChange={formState.setMealTypes}
                onCookingMethodChange={formState.setCookingMethod}
                onPrepTimeChange={(value) => {
                  formState.updatePrepTime(value);
                  const cookTime = typeof formState.cookTimeMinutes === 'number' ? formState.cookTimeMinutes : 0;
                  formState.updateTotalTime(typeof value === 'number' ? value : 0, cookTime);
                }}
                onCookTimeChange={(value) => {
                  formState.updateCookTime(value);
                  const prepTime = typeof formState.prepTimeMinutes === 'number' ? formState.prepTimeMinutes : 0;
                  formState.updateTotalTime(prepTime, typeof value === 'number' ? value : 0);
                }}
                onShareWithOthersChange={formState.setShareWithOthers}
                onRecipeImageFileSelect={handleRecipeImageFileSelect}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            data-action-tour-id="create-recipe-portions"
            value="portions"
            className="relative z-50 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="px-5 py-4 text-left text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)] hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Portion Sizes
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <RecipePortionSizesSection
                portionSizeId={portionSizes.portionSizeId}
                portionSizes={portionSizes.portionSizes}
                defaultServingGrams={portionSizes.defaultServingGrams}
                portionDensityGPerMl={portionSizes.portionDensityGPerMl}
                customPortionName={portionSizes.customPortionName}
                customPortionGrams={portionSizes.customPortionGrams}
                customPortionMl={portionSizes.customPortionMl}
                onPortionSizeChange={portionSizes.setPortionSizeId}
                onDefaultServingGramsChange={portionSizes.setDefaultServingGrams}
                onPortionDensityChange={portionSizes.setPortionDensityGPerMl}
                onPortionWeightChange={portionSizes.setPortionWeight}
                onPortionMlChange={portionSizes.setPortionVolume}
                onPortionQuantityChange={portionSizes.setPortionQuantity}
                onCustomPortionNameChange={portionSizes.setCustomPortionName}
                onCustomPortionGramsChange={portionSizes.setCustomPortionGrams}
                onCustomPortionMlChange={portionSizes.setCustomPortionMl}
                onAddCustomPortion={portionSizes.addCustomPortion}
                onRemovePortion={portionSizes.removePortion}
                getPortionOptionValue={portionSizes.getPortionOptionValue}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            data-action-tour-id="create-recipe-tags"
            value="categories"
            className="relative z-40 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="px-5 py-4 text-left text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)] hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Tags
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <RecipeTagsSection
                allergies={formState.allergies}
                intoleranceTags={formState.intoleranceTags}
                cuisineTypes={formState.cuisineTypes}
                dietLabels={formState.dietLabels}
                lockedAllergies={ingredientDerivedAllergies}
                lockedIntoleranceTags={ingredientDerivedIntolerances}
                onAllergiesChange={handleAllergiesChange}
                onIntoleranceTagsChange={handleIntoleranceTagsChange}
                onCuisineTypesChange={formState.setCuisineTypes}
                onDietLabelsChange={formState.setDietLabels}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            data-demo-id="recipe-ingredients-section"
            data-action-tour-id="create-recipe-ingredients"
            value="ingredients"
            className="relative z-30 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="items-start px-5 py-4 hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="flex flex-col gap-2 text-left">
                <span className="text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)]">
                  Ingredients
                </span>
                <span className="text-sm font-normal leading-[1.334] text-[rgba(0,0,0,0.8)]">
                  Physical information needed for accurate nutritional calculations
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="!overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <IngredientsSection
                ingredients={ingredientManagement.ingredients}
                isAddingIngredient={ingredientManagement.isAddingIngredient}
                ingredientSearchQuery={ingredientManagement.ingredientSearchQuery}
                selectedIngredientName={ingredientManagement.selectedIngredientName}
                selectedYieldFactor={ingredientManagement.selectedYieldFactor}
                selectedRetentionFactor={ingredientManagement.selectedRetentionFactor}
                ingredientAmount={ingredientManagement.ingredientAmount}
                ingredientUnit={ingredientManagement.ingredientUnit}
                unitWeightGrams={ingredientManagement.unitWeightGrams}
                unitWeightMl={ingredientManagement.unitWeightMl}
                effectiveAmountGrams={ingredientManagement.effectiveAmountGrams}
                effectiveAmountMl={ingredientManagement.effectiveAmountMl}
                gramsPerMl={ingredientManagement.gramsPerMl}
                availableUnits={ingredientManagement.availableUnits}
                unitLabelsByKey={ingredientManagement.unitLabelsByKey}
                unitWeightsByKey={ingredientManagement.unitWeightsByKey}
                nutritionData={ingredientManagement.nutritionData}
                isLoadingNutrition={ingredientManagement.isLoadingNutrition}
                isLoadingUnits={ingredientManagement.isLoadingUnits}
                editingIngredientId={ingredientManagement.editingIngredientId}
                showIngredientResults={ingredientManagement.showIngredientResults}
                ingredientSearchResults={ingredientManagement.ingredientSearchResults}
                isSearchingIngredients={ingredientManagement.isSearchingIngredients}
                bulkIngredientInput={ingredientManagement.bulkIngredientInput}
                isBulkAddingIngredients={ingredientManagement.isBulkAddingIngredients}
                selectedIngredientPreviewUrl={ingredientManagement.selectedIngredientPreviewUrl}
                totalMacros={ingredientManagement.totalMacros}
                servings={typeof formState.servings === 'number' ? formState.servings : 1}
                onStartAddIngredient={ingredientManagement.startAddIngredient}
                onSearchQueryChange={ingredientManagement.setIngredientSearchQuery}
                onIngredientSelect={ingredientManagement.handleSelectIngredient}
                onAmountChange={ingredientManagement.handleQuantityChange}
                onUnitChange={ingredientManagement.handleUnitChange}
                onUnitWeightChange={ingredientManagement.handleUnitWeightChange}
                onUnitWeightMlChange={ingredientManagement.handleUnitWeightMlChange}
                onCloseDropdown={ingredientManagement.handleCloseIngredientDropdown}
                onSubmitIngredient={ingredientManagement.handleSubmitIngredient}
                onCancelIngredient={ingredientManagement.closeIngredientForm}
                onEditIngredient={ingredientManagement.handleEditIngredient}
                onRemoveIngredient={ingredientManagement.handleRemoveIngredient}
                onBulkIngredientInputChange={ingredientManagement.setBulkIngredientInput}
                onBulkAddIngredients={ingredientManagement.handleBulkAddIngredients}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            data-demo-id="recipe-instructions-section"
            data-action-tour-id="create-recipe-instructions"
            value="instructions"
            className="relative z-20 overflow-visible rounded-[20px] border border-[#F5F5F5] bg-white shadow-sm"
          >
            <AccordionTrigger
              className="items-start px-5 py-4 hover:no-underline sm:px-5"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <div className="flex flex-col gap-2 text-left">
                <span className="text-[24px] font-medium leading-[1.334] text-[rgba(0,0,0,0.87)]">
                  Instructions
                </span>
                <span className="text-sm font-normal leading-[1.334] text-[rgba(0,0,0,0.8)]">
                  Describe how to prepare the recipe and any optional tips.
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="overflow-visible px-5 pb-[26px] pt-5 sm:px-5">
              <InstructionsSection
                instructions={formState.instructions}
                cookingTips={formState.cookingTips}
                onInstructionsChange={formState.setInstructions}
                onCookingTipsChange={formState.setCookingTips}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-3 text-xs text-slate-500 hover:bg-slate-100"
            onClick={() => {
              if (isFromMealPlan) {
                handleBackToMealPlan();
                return;
              }
              navigate('/recipes');
            }}
          >
            Cancel
          </Button>
          <Button
            data-demo-id="recipe-create"
            data-action-tour-id="create-recipe-submit"
            onClick={handleCreateRecipe}
            disabled={
              formState.recipeName.trim().length === 0
              || formState.mealTypes.length === 0
              || ingredientManagement.ingredients.length === 0
              || isCreating
            }
            className="h-8 bg-[#2EB4E4] px-3 text-xs font-semibold text-white hover:bg-[#22a8d8]"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <ChefHat className="mr-2 h-4 w-4" />
                Create Recipe
              </>
            )}
          </Button>
        </div>
            </div>
          </div>
        </div>
      </Paper>
      <AlertDialog
        open={Boolean(duplicateRecipeMessage)}
        onOpenChange={(open) => {
          if (!open) clearDuplicateRecipeMessage();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recipe name already exists</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicateRecipeMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={clearDuplicateRecipeMessage}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ActionTour
        ariaLabel="Create recipe guide"
        label="Recipe guide"
        storageKey="caloriescience:create-recipe-guide:v1"
        steps={createRecipeActionTourSteps}
      />
    </Box>
  );
};

export default CreateCustomRecipePage;
  
